import { useSyncExternalStore } from 'react';

/**
 * Below this width the dashboard is the PHONE screen: a layout of its own, and
 * view-only.
 *
 * 600, because it is where the dispatch dashboard's own app turns into its
 * phone shell (`AppBreakpoints.compact` in dispatch-dashboard). Framed there,
 * this page is as wide as the phone — so the two apps change layout at the
 * same moment, and a phone never shows the host's phone chrome around a
 * desktop grid. Standalone, it is the same rule for the same reason.
 *
 * Measured on this document's own viewport rather than asked of the host: the
 * iframe IS the width the host gave it, so the answer cannot disagree with the
 * layout it has to fit, and the standalone console needs no host to ask.
 */
export const COMPACT_BELOW = 600;

/** Just under the breakpoint, so a viewport of exactly 600 is the wide layout — as it is in Flutter. */
export const COMPACT_QUERY = `(max-width: ${COMPACT_BELOW - 0.02}px)`;

const listFor = (): MediaQueryList | null => {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(COMPACT_QUERY)
      : null;
  } catch {
    return null;
  }
};

const subscribe = (onChange: () => void) => {
  const list = listFor();
  if (!list) return () => {};
  list.addEventListener('change', onChange);
  return () => list.removeEventListener('change', onChange);
};

const snapshot = (): boolean => listFor()?.matches === true;

/** Whether the page is phone-width now, and again every time that changes. */
export function useCompact(): boolean {
  // A browser with no matchMedia at all is not a phone anyone can buy: wide.
  return useSyncExternalStore(subscribe, snapshot, () => false);
}
